"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createNftCollection = exports.prepareStorage = exports.prepareStorageForTickerDetails = exports.assertNftCollectionDoesNotExists = exports.getAuthorization = exports.prepareCreateNftCollection = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const lodash_1 = require("lodash");
const internal_1 = require("../../internal");
const types_1 = require("../../types");
const conversion_1 = require("../../utils/conversion");
const internal_2 = require("../../utils/internal");
/**
 * @hidden
 */
function assertTickerOk(ticker) {
    if (!(0, internal_2.isAllowedCharacters)(ticker)) {
        throw new internal_1.PolymeshError({
            code: types_1.ErrorCode.ValidationError,
            message: 'New Tickers can only contain alphanumeric values "_", "-", ".", and "/"',
        });
    }
}
/**
 * @hidden
 */
function isLocalMetadata(value) {
    return value.type === types_1.MetadataType.Local;
}
/**
 * @hidden
 */
function isGlobalMetadata(value) {
    return value.type === types_1.MetadataType.Global;
}
/**
 * @hidden
 */
function getCreateAssetTxAndFees(context, storage, args) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const { polymeshApi: { tx }, } = context;
        const { customTypeData, assetId } = storage;
        const { ticker, nftType, name, securityIdentifiers = [], fundingRound } = args;
        let fee;
        const internalNftType = (0, conversion_1.getInternalNftType)(customTypeData, nftType);
        const rawName = (0, conversion_1.nameToAssetName)((_a = name !== null && name !== void 0 ? name : ticker) !== null && _a !== void 0 ? _a : assetId, context);
        const rawDivisibility = (0, conversion_1.booleanToBool)(false, context);
        const rawIdentifiers = securityIdentifiers.map(identifier => (0, conversion_1.securityIdentifierToAssetIdentifier)(identifier, context));
        const rawFundingRound = (0, internal_2.optionize)(conversion_1.stringToBytes)(fundingRound, context);
        const rawAssetType = (0, conversion_1.internalAssetTypeToAssetType)({ NonFungible: internalNftType }, context);
        return (0, internal_2.checkTxType)({
            transaction: tx.asset.createAsset,
            fee,
            args: [rawName, rawDivisibility, rawAssetType, rawIdentifiers, rawFundingRound],
        });
    });
}
/**
 * @hidden
 */
function getTickerRelatedTx(ticker, status, rawAssetId, context) {
    const { polymeshApi: { tx }, } = context;
    const rawTicker = (0, conversion_1.stringToTicker)(ticker, context);
    const transactions = [];
    if (status === types_1.TickerReservationStatus.Free) {
        transactions.push((0, internal_2.checkTxType)({
            transaction: tx.asset.registerUniqueTicker,
            args: [rawTicker],
        }));
    }
    if (status !== types_1.TickerReservationStatus.AssetCreated) {
        transactions.push((0, internal_2.checkTxType)({
            transaction: tx.asset.linkTickerToAssetId,
            args: [rawTicker, rawAssetId],
        }));
    }
    return transactions;
}
/**
 * @hidden
 */
function prepareCreateNftCollection(args) {
    return __awaiter(this, void 0, void 0, function* () {
        const { context: { polymeshApi: { tx }, }, context, storage: { customTypeData, status, assetId, isAssetCreated }, storage, } = this;
        const { ticker, nftType, collectionKeys, documents } = args;
        let transactions = [];
        if (ticker) {
            assertTickerOk(ticker);
        }
        const internalNftType = (0, conversion_1.getInternalNftType)(customTypeData, nftType);
        const nftCollection = new internal_1.NftCollection({ assetId }, context);
        const rawAssetId = (0, conversion_1.assetToMeshAssetId)(nftCollection, context);
        const rawType = (0, conversion_1.internalNftTypeToNftType)(internalNftType, context);
        let nextLocalId = new bignumber_js_1.default(1);
        if (isAssetCreated) {
            /**
             * assets can be created with type Nft, but not have a created collection,
             * we handle this case to prevent a ticker getting stuck if it was initialized via non SDK methods
             */
            const asset = new internal_1.FungibleAsset({ assetId }, context);
            let nonFungible;
            [{ nonFungible }, nextLocalId] = yield Promise.all([
                asset.details(),
                asset.metadata.getNextLocalId(),
            ]);
            if (!nonFungible) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.UnmetPrerequisite,
                    message: 'Only assets with type NFT can be turned into NFT collections',
                });
            }
        }
        else {
            if (storage.customTypeData && !storage.customTypeData.isAlreadyCreated) {
                transactions.push((0, internal_2.checkTxType)({
                    transaction: tx.asset.registerCustomAssetType,
                    args: [storage.customTypeData.rawValue],
                }));
            }
            transactions.push(yield getCreateAssetTxAndFees(context, storage, args));
        }
        if (ticker && status) {
            transactions = [...transactions, ...getTickerRelatedTx(ticker, status, rawAssetId, context)];
        }
        const globalMetadataKeys = collectionKeys.filter(isGlobalMetadata);
        const localMetadataKeys = collectionKeys.filter(isLocalMetadata);
        localMetadataKeys.forEach(localKey => {
            const { name: metaName, spec } = localKey;
            const rawMetadataName = (0, conversion_1.stringToBytes)(metaName, context);
            const rawSpec = (0, conversion_1.metadataSpecToMeshMetadataSpec)(spec, context);
            transactions.push((0, internal_2.checkTxType)({
                transaction: tx.asset.registerAssetMetadataLocalType,
                args: [rawAssetId, rawMetadataName, rawSpec],
            }));
        });
        const keyValues = [
            ...globalMetadataKeys,
            ...localMetadataKeys.map((key, index) => {
                return {
                    type: types_1.MetadataType.Local,
                    id: nextLocalId.plus(index),
                };
            }),
        ];
        const rawCollectionKeys = (0, conversion_1.collectionKeysToMetadataKeys)(keyValues, context);
        if (documents === null || documents === void 0 ? void 0 : documents.length) {
            const rawDocuments = documents.map(doc => (0, conversion_1.assetDocumentToDocument)(doc, context));
            const feeMultiplier = new bignumber_js_1.default(rawDocuments.length);
            transactions.push((0, internal_2.checkTxType)({
                transaction: tx.asset.addDocuments,
                feeMultiplier,
                args: [rawDocuments, rawAssetId],
            }));
        }
        transactions.push((0, internal_2.checkTxType)({
            transaction: tx.nft.createNftCollection,
            args: [rawAssetId, rawType, rawCollectionKeys],
        }));
        return {
            transactions,
            resolver: nftCollection,
        };
    });
}
exports.prepareCreateNftCollection = prepareCreateNftCollection;
/**
 * @hidden
 */
function getAuthorization({ ticker, documents }) {
    return __awaiter(this, void 0, void 0, function* () {
        const { storage: { status, needsLocalMetadata, isAssetCreated, assetId, customTypeData }, context, } = this;
        const transactions = [types_1.TxTags.nft.CreateNftCollection];
        if (customTypeData && !customTypeData.isAlreadyCreated) {
            transactions.push(types_1.TxTags.asset.RegisterCustomAssetType);
        }
        if (!isAssetCreated) {
            transactions.push(types_1.TxTags.asset.CreateAsset);
        }
        if (status === types_1.TickerReservationStatus.Free) {
            transactions.push(types_1.TxTags.asset.RegisterUniqueTicker);
        }
        if (status !== types_1.TickerReservationStatus.AssetCreated) {
            transactions.push(types_1.TxTags.asset.LinkTickerToAssetId);
        }
        if (needsLocalMetadata) {
            transactions.push(types_1.TxTags.asset.RegisterAssetMetadataLocalType);
        }
        if (documents === null || documents === void 0 ? void 0 : documents.length) {
            transactions.push(types_1.TxTags.asset.AddDocuments);
        }
        const permissions = {
            transactions,
            assets: [],
            portfolios: [],
        };
        if (ticker && status && status === types_1.TickerReservationStatus.Reserved) {
            return {
                permissions,
                roles: [{ type: types_1.RoleType.TickerOwner, ticker }],
            };
        }
        if (isAssetCreated) {
            return {
                permissions: Object.assign(Object.assign({}, permissions), { assets: [new internal_1.BaseAsset({ assetId }, context)] }),
            };
        }
        return { permissions };
    });
}
exports.getAuthorization = getAuthorization;
/**
 * @hidden
 */
function assertNftCollectionDoesNotExists(id, context, errorParams) {
    return __awaiter(this, void 0, void 0, function* () {
        const nft = new internal_1.NftCollection({ assetId: id }, context);
        const collectionExists = yield nft.exists();
        if (collectionExists) {
            throw new internal_1.PolymeshError(errorParams);
        }
    });
}
exports.assertNftCollectionDoesNotExists = assertNftCollectionDoesNotExists;
/**
 * @hidden
 */
function prepareStorageForTickerDetails(context, ticker, assetId) {
    return __awaiter(this, void 0, void 0, function* () {
        assertTickerOk(ticker);
        const storageStatus = {
            isAssetCreated: false,
            assetId: '',
        };
        const reservation = new internal_1.TickerReservation({ ticker }, context);
        const reservationDetails = yield reservation.details();
        const { status } = reservationDetails;
        storageStatus.status = status;
        if (status === types_1.TickerReservationStatus.AssetCreated) {
            storageStatus.assetId = reservationDetails.assetId;
            storageStatus.isAssetCreated = true;
            if (assetId && assetId !== reservationDetails.assetId) {
                // For v7 chain, if assetID is provided in arguments, and the ticker provided is not already associated with the given asset ID, throw an error
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.UnmetPrerequisite,
                    message: 'Ticker is already linked to another asset',
                    data: {
                        ticker,
                        linkedAssetId: reservationDetails.assetId,
                    },
                });
            }
            yield assertNftCollectionDoesNotExists(reservationDetails.assetId, context, {
                code: types_1.ErrorCode.UnmetPrerequisite,
                message: 'An NFT collection already exists with the ticker',
                data: { ticker },
            });
        }
        else {
            storageStatus.assetId = yield context.getSigningAccount().getNextAssetId();
        }
        return storageStatus;
    });
}
exports.prepareStorageForTickerDetails = prepareStorageForTickerDetails;
/**
 * @hidden
 */
function prepareStorage({ ticker, assetId, nftType, collectionKeys }) {
    return __awaiter(this, void 0, void 0, function* () {
        const { context } = this;
        let storageStatus = {
            isAssetCreated: false,
            assetId: '',
        };
        if (ticker) {
            storageStatus = yield prepareStorageForTickerDetails(context, ticker, assetId);
        }
        if (assetId) {
            storageStatus.assetId = assetId;
            storageStatus.isAssetCreated = true;
            yield assertNftCollectionDoesNotExists(assetId, context, {
                code: types_1.ErrorCode.UnmetPrerequisite,
                message: 'An NFT collection already exists with the given asset ID',
                data: { assetId },
            });
        }
        else {
            storageStatus.assetId = yield context.getSigningAccount().getNextAssetId();
        }
        const needsLocalMetadata = collectionKeys.some(isLocalMetadata);
        const customTypeData = yield (0, internal_2.prepareStorageForCustomType)(nftType, (0, lodash_1.values)(types_1.KnownNftType), context, 'createNftCollection');
        return Object.assign({ customTypeData,
            needsLocalMetadata }, storageStatus);
    });
}
exports.prepareStorage = prepareStorage;
/**
 * @hidden
 */
const createNftCollection = () => new internal_1.Procedure(prepareCreateNftCollection, getAuthorization, prepareStorage);
exports.createNftCollection = createNftCollection;
//# sourceMappingURL=createNftCollection.js.map